import { FC, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, IconButton, Button } from '@mui/material';
import { ContentCopy, LinkedIn, X, Email, BookmarkBorder, Bookmark } from '@mui/icons-material';

import { Post } from '@libs/models';
import { ApplicationSource } from '@libs/types';
import { Appcast, Talroo, TalrooPixel } from '@carcareers/components';
import { useApplyMutation, useSaveMutation, useUnapplyMutation } from '@carcareers/store';
import { useAuth, useLocalStorage } from '@carcareers/hooks';
import { useSnackbar } from '@carcareers/contexts';

interface JobseekerPostActionsProps {
  post?: Post | null;
  applied?: boolean;
  saved?: boolean;
  disabled?: boolean;
  onApplySuccess?: (postId: number) => void;
  onUnapplySuccess?: (postId: number) => void;
  onSaveSuccess?: (postId: number) => void;
}

export const JobseekerPostActions: FC<JobseekerPostActionsProps> = ({
  post,
  applied,
  saved,
  disabled,
  onApplySuccess,
  onUnapplySuccess,
  onSaveSuccess,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useAuth();
  const { toggleSnackbar } = useSnackbar();
  const [applyToJob, { isLoading: isApplying }] = useApplyMutation();
  const [unapplyToJob, { isLoading: isUnapplying }] = useUnapplyMutation();
  const [saveJob, { isLoading: isSaving }] = useSaveMutation();
  const [postId, setPostId] = useLocalStorage<number>('post_id', undefined, true);
  const [source, setSource] = useLocalStorage<ApplicationSource>('source', searchParams.get('source') as ApplicationSource ?? ApplicationSource.CARCAREERS, true);

  const url = `${window.location.origin}/jobs/${post?.id}`;
  const title = post?.job?.title ?? '';
  const summary = post?.job?.description ?? '';

  const checkAuth = useCallback(() => {
    if (!session.auth?.token) {
      if (post?.id && postId !== post.id) {
        setPostId(post.id);
      }

      navigate('/auth/login');
      return false;
    } else if (session?.user?.role !== 'jobseeker') {
      toggleSnackbar('error', 'You must be a jobseeker to apply or save this job.');
      return false;
    }
    return true;
  }, [post, postId, session.auth?.token, session?.user?.role, navigate, setPostId, toggleSnackbar]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(url);
  }, [url]);

  const handleShareLinkedIn = useCallback(() => {
    window.open(
      `https://www.linkedin.com/shareArticle?url=${url}&title=${title}&summary=${summary}&source=CarCareers`,
      '_blank',
    );
  }, [url, title, summary]);

  const handleShareX = useCallback(() => {
    window.open(`https://x.com/intent/tweet?url=${url}&text=${title}`, '_blank');
  }, [url, title]);

  const handleShareEmail = useCallback(() => {
    window.open(`mailto:?subject=${title}&body=${url}`, '_blank');
  }, [url, title]);

  const handleUnapply = useCallback(async () => {
    if (!checkAuth() || !post?.id) {
      return;
    }

    try {
      const response = await unapplyToJob(post.id);

      if ('data' in response) {
        toggleSnackbar('success', 'Successfully unapplied!');

        if (onUnapplySuccess) {
          onUnapplySuccess(post.id);
        }
      }
    } catch (error) {
      toggleSnackbar(
        'error',
        'Something went wrong. Please try again. If the problem persists, please contact support.',
      );
    }
  }, [unapplyToJob, checkAuth, onUnapplySuccess, post?.id, toggleSnackbar]);

  const handleApply = useCallback(async () => {
    if (!checkAuth() || !post?.id) {
      return;
    }

    try {
      const response = await applyToJob({ postId: post.id, source });

      if ('data' in response) {
        toggleSnackbar(
          'success',
          <>
            Applied!
            <Appcast />
            <Talroo pixel={TalrooPixel.APPPLIED} />
          </>,
        );

        if (onApplySuccess) {
          onApplySuccess(post.id);
        }
      } else {
        toggleSnackbar(
          'error',
          'Something went wrong. Please try again. If the problem persists, please contact support.',
        );
      }
    } catch (error) {
      toggleSnackbar(
        'error',
        'Something went wrong. Please try again. If the problem persists, please contact support.',
      );
    }
  }, [applyToJob, checkAuth, onApplySuccess, post?.id, toggleSnackbar]);

  const handleSave = useCallback(async () => {
    if (!checkAuth() || !post?.id) {
      return;
    }

    try {
      const response = await saveJob(post.id);

      if ('data' in response) {
        if (saved) {
          toggleSnackbar('success', 'Job removed from saved jobs!');
        } else {
          toggleSnackbar('success', 'Job saved successfully!');
        }

        if (onSaveSuccess) {
          onSaveSuccess(post.id);
        }
      } else {
        toggleSnackbar(
          'error',
          'Something went wrong. Please try again. If the problem persists, please contact support.',
        );
      }
    } catch (error) {
      toggleSnackbar(
        'error',
        'Something went wrong. Please try again. If the problem persists, please contact support.',
      );
    }
  }, [saveJob, checkAuth, onSaveSuccess, post?.id, toggleSnackbar, saved]);

  useEffect(() => {
    if (searchParams.get('source')) {
      setSource(searchParams.get('source') as ApplicationSource);
    }
  }, [searchParams, setSource]);

  return (
    <>
      {/* <Helmet>
        <title>CARCAREERS.COM - {title}</title>
        <meta name="description" content={summary} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={summary} />
        <meta
          property="og:image"
          content={'//https://storage.googleapis.com/carcareerrs-cloud-storage-assets/logo-icon.png'}
        />
        <meta property="og:url" content={`//${url}`} />
      </Helmet> */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'flex-start' },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
          <IconButton color="primary" aria-label="copy URL" title="Copy URL" onClick={handleCopy} disabled={disabled}>
            <ContentCopy sx={{ width: '15px', height: '15px' }} />
          </IconButton>
          <IconButton
            color="primary"
            aria-label="share on LinkedIn"
            title="Share on LinkedIn"
            onClick={handleShareLinkedIn}
            disabled={disabled}
          >
            <LinkedIn sx={{ width: '15px', height: '15px' }} />
          </IconButton>
          <IconButton
            color="primary"
            aria-label="share on X"
            title="Share on X"
            onClick={handleShareX}
            disabled={disabled}
          >
            <X sx={{ width: '15px', height: '15px' }} />
          </IconButton>
          <IconButton
            color="primary"
            aria-label="share using email"
            title="Share using email"
            onClick={handleShareEmail}
            disabled={disabled}
          >
            <Email sx={{ width: '15px', height: '15px' }} />
          </IconButton>
        </Box>
      </Box>
      {applied ? (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mr: 1 }}
          disabled={isApplying || isUnapplying || disabled}
          onClick={handleUnapply}
          title="Click to unapply"
        >
          Applied
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ mr: 1 }}
          onClick={handleApply}
          disabled={isApplying || isUnapplying || disabled}
        >
          Apply
        </Button>
      )}
      <IconButton
        color="secondary"
        aria-label={saved ? 'unsave job' : 'save job'}
        onClick={handleSave}
        disabled={isSaving || disabled}
        title={saved ? 'Remove from saved jobs' : 'Save job'}
      >
        {saved ? <Bookmark /> : <BookmarkBorder />}
      </IconButton>
    </>
  );
};
